/**
 * Owner: Masa Bayu
 * File: BecomeAnAuthorScreen.tsx
 * Date:Time: 2026-10-29 10:40
 * Description: Jadi penulis pada akaun QX yang sama (sesi / log masuk), bukan e-mel baharu.
 */

import { useMemo, useState, type ReactElement, type SyntheticEvent } from 'react';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import { getMarketplaceLocaleProfile } from '../../home/marketplace/marketplace-locale';
import {
  HubSessionError,
  becomePublisher,
  becomePublisherWithPassword,
  registerWithPassword,
  type HubSessionView,
} from '../../auth/hub-session';
import { useHubAuthState } from '../../publication/hub-auth';
import { useHubSession } from '../../auth/use-hub-session';
import { resetPublisherDashboard } from '../private/publisher-dashboard';
import { STUDIO_ENTRY } from '../author-production-identity';
import { resolveBecomeAuthorLabels, type BecomeAuthorLabels } from './become-author-labels';
import {
  HUB_MIN_PASSWORD_LENGTH,
  readPublisherAccountId,
  storePublisherAccountId,
  type PublisherSignupError,
} from './publisher-signup';
import './become-an-author.css';

export interface BecomeAnAuthorScreenProps {
  readonly onNavigate: (path: string) => void;
}

function isPublisherSession(roles: readonly string[] | undefined): boolean {
  return roles?.includes('publisher') === true || roles?.includes('author') === true;
}

export function BecomeAnAuthorScreen({ onNavigate }: BecomeAnAuthorScreenProps): ReactElement {
  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const t = useMemo(() => resolveBecomeAuthorLabels(profile.language), [profile.language]);
  const hub = useHubSession();
  const hubAuth = useHubAuthState();
  const waitSession = hubAuth === 'signed-in' && !hub.sessionReady;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<PublisherSignupError | null>(null);
  const [pending, setPending] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(() => readPublisherAccountId());

  const signedEmail = hub.authSession?.account?.user.email ?? '';
  const signedId = hub.authSession?.account?.user.id ?? '';
  const alreadyPublisher = isPublisherSession(hub.authSession?.account?.roles);
  const doneId = accountId ?? (alreadyPublisher && signedId.length > 0 ? signedId : null);

  function finish(id: string): void {
    storePublisherAccountId(id);
    setAccountId(id);
    setPassword('');
    resetPublisherDashboard();
  }

  async function run(task: () => Promise<HubSessionView>): Promise<void> {
    setPending(true);
    setError(null);
    try {
      const session = await task();
      const id = session.account?.user.id ?? '';
      if (id.length === 0) throw new HubSessionError('Respons identiti tiada akaun', 500);
      hub.onSignedIn(session);
      finish(id);
    } catch (caught) {
      setError(mapIdentityError(caught));
    } finally {
      setPending(false);
    }
  }

  function onLoginSubmit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() => becomePublisherWithPassword({ email, password }));
  }

  function onCreateSubmit(): void {
    void run(() =>
      registerWithPassword({
        email,
        password,
        displayName: email.trim(),
        role: 'publisher',
      }),
    );
  }

  function onContinueSignedIn(): void {
    void run(() => becomePublisher());
  }

  const message = error === null ? null : labelForError(error, t);

  return (
    <main className="qxb" aria-labelledby="qxb-title">
      <header className="qxb__hero">
        <p className="qxb__eyebrow">{t.eyebrow}</p>
        <h1 id="qxb-title" className="qxb__title">
          {t.title}
        </h1>
        <p className="qxb__lede">{t.sub}</p>
      </header>

      <div className="qxb__body">
        <section className="qxb__card qxb__steps" aria-label={t.ariaSteps}>
          <h2 className="qxb__card-title">{t.stepsTitle}</h2>
          <ol className="qxb__steplist">
            {t.steps.map((step) => (
              <li key={step.title} className="qxb__step">
                <span className="qxb__stepicon" aria-hidden="true">
                  <LandingIcon name="pen" size={18} />
                </span>
                <span className="qxb__steptext">
                  <strong>{step.title}</strong>
                  <small>{step.sub}</small>
                </span>
              </li>
            ))}
          </ol>
        </section>

        <section className="qxb__card qxb__formcard" aria-label={t.ariaForm}>
          {doneId !== null ? (
            <DoneState t={t} accountId={doneId} onNavigate={onNavigate} />
          ) : waitSession ? (
            <p className="qxb__card-sub">{t.submitting}</p>
          ) : hub.signedIn ? (
            <SignedInContinue
              t={t}
              email={signedEmail}
              pending={pending}
              message={message}
              onContinue={onContinueSignedIn}
            />
          ) : (
            <GuestForm
              t={t}
              email={email}
              password={password}
              pending={pending}
              message={message}
              onEmail={setEmail}
              onPassword={setPassword}
              onLogin={onLoginSubmit}
              onCreate={onCreateSubmit}
            />
          )}
        </section>
      </div>
    </main>
  );
}

function SignedInContinue({
  t,
  email,
  pending,
  message,
  onContinue,
}: {
  readonly t: BecomeAuthorLabels;
  readonly email: string;
  readonly pending: boolean;
  readonly message: string | null;
  readonly onContinue: () => void;
}): ReactElement {
  return (
    <div className="qxb__session">
      <h2 className="qxb__card-title">{t.sessionTitle}</h2>
      <p className="qxb__card-sub">{t.sessionSub}</p>
      {email.length > 0 ? (
        <p className="qxb__idrow">
          <span>{t.sessionEmailLabel}</span>
          <code>{email}</code>
        </p>
      ) : null}
      {message !== null ? (
        <p className="qxb__error" role="alert">
          {message}
        </p>
      ) : null}
      <button type="button" className="qxb__submit" disabled={pending} onClick={onContinue}>
        {pending ? t.submitting : t.submit} <LandingIcon name="arrowRight" size={15} />
      </button>
    </div>
  );
}

function GuestForm({
  t,
  email,
  password,
  pending,
  message,
  onEmail,
  onPassword,
  onLogin,
  onCreate,
}: {
  readonly t: BecomeAuthorLabels;
  readonly email: string;
  readonly password: string;
  readonly pending: boolean;
  readonly message: string | null;
  readonly onEmail: (value: string) => void;
  readonly onPassword: (value: string) => void;
  readonly onLogin: (event: SyntheticEvent<HTMLFormElement>) => void;
  readonly onCreate: () => void;
}): ReactElement {
  return (
    <>
      <h2 className="qxb__card-title">{t.formTitle}</h2>
      <p className="qxb__card-sub">{t.formSub}</p>
      <form className="qxb__form" onSubmit={onLogin}>
        <label className="qxb__field">
          <span>{t.emailLabel}</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => {
              onEmail(event.target.value);
            }}
          />
        </label>
        <label className="qxb__field">
          <span>{t.passwordLabel}</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            minLength={HUB_MIN_PASSWORD_LENGTH}
            required
            value={password}
            onChange={(event) => {
              onPassword(event.target.value);
            }}
          />
          <small className="qxb__hint">{t.passwordHint}</small>
        </label>
        {message !== null ? (
          <p className="qxb__error" role="alert">
            {message}
          </p>
        ) : null}
        <button type="submit" className="qxb__submit" disabled={pending}>
          {pending ? t.submitting : t.submit} <LandingIcon name="arrowRight" size={15} />
        </button>
        <button type="button" className="qxb__ghost" disabled={pending} onClick={onCreate}>
          {t.createSubmit}
        </button>
      </form>
    </>
  );
}

function DoneState({
  t,
  accountId,
  onNavigate,
}: {
  readonly t: BecomeAuthorLabels;
  readonly accountId: string;
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  return (
    <div className="qxb__done">
      <span className="qxb__doneicon" aria-hidden="true">
        <LandingIcon name="checkCircle" size={26} />
      </span>
      <h2 className="qxb__card-title">{t.successTitle}</h2>
      <p className="qxb__card-sub">{t.successSub}</p>
      <p className="qxb__idrow">
        <span>{t.accountIdLabel}</span>
        <code>{accountId}</code>
      </p>
      <div className="qxb__doneactions">
        <button
          type="button"
          className="qxb__submit"
          onClick={() => {
            onNavigate(STUDIO_ENTRY);
          }}
        >
          {t.openEditor} <LandingIcon name="arrowRight" size={15} />
        </button>
        <button
          type="button"
          className="qxb__ghost"
          onClick={() => {
            onNavigate('/author/dashboard');
          }}
        >
          {t.goDashboard}
        </button>
      </div>
    </div>
  );
}

function mapIdentityError(error: unknown): PublisherSignupError {
  if (error instanceof HubSessionError) {
    if (error.status === 409) return 'email-taken';
    if (error.status === 401) return 'bad-credentials';
    if (error.status === 403) return 'forbidden';
    if (error.status === 422) return 'invalid';
  }
  return 'unavailable';
}

function labelForError(error: PublisherSignupError, t: BecomeAuthorLabels): string {
  if (error === 'email-taken') return t.errorEmailTaken;
  if (error === 'invalid') return t.errorInvalid;
  if (error === 'bad-credentials') return t.errorBadCredentials;
  if (error === 'forbidden') return t.errorForbidden;
  return t.errorUnavailable;
}
